/**
 * IAM — React Query hooks for User Directory, Roles, Audit.
 * API base: /api/iam (proxied via cpFetch → /cp/api/iam)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cpFetch } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';

/* ─── Types ─── */

export interface IamUser {
  user_id: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  roles: string[];
  tenant_id: string;
  status: 'active' | 'disabled';
  auth_provider: string;
  last_login_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface IamRole {
  id: string;
  name: string;
  description: string;
  color: string;
}

export interface AuditEntry {
  id: string;
  action: string;
  actor_id: string;
  target_id: string | null;
  details: Record<string, any>;
  tenant_id: string;
  timestamp: string;
}

export interface IamStats {
  total: number;
  active: number;
  disabled: number;
  role_distribution: Record<string, number>;
  recent_audit: AuditEntry[];
}

export interface IamUserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
}

export interface AuditFilters {
  page?: number;
  limit?: number;
  action?: string;
  actor?: string;
  date_from?: string;
  date_to?: string;
}

/* ─── Hooks ─── */

/** List all platform users (merged Firebase + platform_users) */
export function useIamUsers(filters: IamUserFilters = {}) {
  const { token } = useAuthStore();
  return useQuery({
    queryKey: ['iam', 'users', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.search) params.set('search', filters.search);
      if (filters.role) params.set('role', filters.role);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      const res = await cpFetch(`/api/iam/users${qs ? `?${qs}` : ''}`);
      return {
        users: (res?.users ?? []) as IamUser[],
        total: res?.pagination?.total ?? 0,
        page: res?.pagination?.page ?? 1,
        limit: res?.pagination?.limit ?? 50,
        pages: res?.pagination?.pages ?? 1,
      };
    },
    enabled: !!token,
    staleTime: 15_000,
  });
}

/** Single user detail */
export function useIamUser(id: string | undefined) {
  const { token } = useAuthStore();
  return useQuery({
    queryKey: ['iam', 'user', id],
    queryFn: async () => {
      const res = await cpFetch(`/api/iam/users/${id}`);
      return (res?.user ?? res) as IamUser;
    },
    enabled: !!token && !!id,
    staleTime: 10_000,
  });
}

/** IAM stats */
export function useIamStats() {
  const { token } = useAuthStore();
  return useQuery<IamStats>({
    queryKey: ['iam', 'stats'],
    queryFn: async () => {
      const res = await cpFetch('/api/iam/stats');
      return res?.stats ?? res;
    },
    enabled: !!token,
    staleTime: 30_000,
  });
}

/** Available global roles */
export function useIamRoles() {
  const { token } = useAuthStore();
  return useQuery<IamRole[]>({
    queryKey: ['iam', 'roles'],
    queryFn: async () => {
      const res = await cpFetch('/api/iam/roles');
      return res?.roles ?? [];
    },
    enabled: !!token,
    staleTime: 300_000,
  });
}

/** Invite user (create Firebase + platform_users) */
export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string; displayName?: string; role?: string }) => {
      return await cpFetch('/api/iam/users/invite', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['iam'] });
    },
  });
}

/** Update user global role */
export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      return await cpFetch(`/api/iam/users/${id}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['iam'] });
    },
  });
}

/** Disable user */
export function useDisableUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await cpFetch(`/api/iam/users/${id}/disable`, { method: 'PUT' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['iam'] });
    },
  });
}

/** Enable user */
export function useEnableUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await cpFetch(`/api/iam/users/${id}/enable`, { method: 'PUT' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['iam'] });
    },
  });
}

/** App-level roles for a user */
export function useUserAppRoles(id: string | undefined) {
  const { token } = useAuthStore();
  return useQuery({
    queryKey: ['iam', 'app-roles', id],
    queryFn: async () => {
      const res = await cpFetch(`/api/iam/users/${id}/app-roles`);
      return res?.app_roles ?? [];
    },
    enabled: !!token && !!id,
    staleTime: 15_000,
  });
}

/** Paginated audit log */
export function useAuditLog(filters: AuditFilters = {}) {
  const { token } = useAuthStore();
  return useQuery({
    queryKey: ['iam', 'audit', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.action) params.set('action', filters.action);
      if (filters.actor) params.set('actor', filters.actor);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const qs = params.toString();
      const res = await cpFetch(`/api/iam/audit${qs ? `?${qs}` : ''}`);
      return {
        entries: (res?.entries ?? []) as AuditEntry[],
        total: res?.pagination?.total ?? 0,
        page: res?.pagination?.page ?? 1,
        pages: res?.pagination?.pages ?? 1,
      };
    },
    enabled: !!token,
    staleTime: 10_000,
  });
}
